import { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Send, CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { useI18n } from '@/contexts/I18nContext';

interface ContactFormProps {
  /** Role-specific request categories shown in the dropdown. */
  categories: string[];
}

/** Contact form — submits to the backend, which emails QuBox support. */
export function ContactForm({ categories }: ContactFormProps) {
  const { tp } = useI18n();
  const s = tp.support;

  const [category, setCategory] = useState('');
  const [subject, setSubject] = useState('');
  const [relatedRef, setRelatedRef] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!category || !subject.trim() || !message.trim()) {
      toast.error(s.validationError);
      return;
    }

    setSubmitting(true);
    try {
      // Backend returns HTTP 200 even for non-auth errors — must check res.success.
      const res = await api.post('/api/v1/support/contact', {
        category,
        subject: subject.trim(),
        message: message.trim(),
        ...(relatedRef.trim() && { relatedRef: relatedRef.trim() }),
      });
      if (!res.success) {
        toast.error(res.message || s.errorMessage);
        return;
      }
      setSent(true);
    } catch {
      toast.error(s.errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setCategory('');
    setSubject('');
    setRelatedRef('');
    setMessage('');
    setSent(false);
  };

  if (sent) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12 text-center">
          <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-green-100 text-green-600 dark:bg-green-950">
            <CheckCircle2 className="h-7 w-7" />
          </div>
          <h3 className="mb-1 text-lg font-semibold">{s.successTitle}</h3>
          <p className="mb-6 max-w-sm text-sm text-muted-foreground">
            {s.successMessage}
          </p>
          <Button variant="outline" onClick={reset}>
            {s.sendAnother}
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">{s.formTitle}</CardTitle>
        <CardDescription>{s.formDescription}</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>{s.categoryLabel}</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger>
                <SelectValue placeholder={s.categoryPlaceholder} />
              </SelectTrigger>
              <SelectContent>
                {categories.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="support-subject">{s.subjectLabel}</Label>
            <Input
              id="support-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={s.subjectPlaceholder}
              maxLength={120}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="support-ref">{s.relatedRefLabel}</Label>
            <Input
              id="support-ref"
              value={relatedRef}
              onChange={(e) => setRelatedRef(e.target.value)}
              placeholder={s.relatedRefPlaceholder}
              maxLength={100}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="support-message">{s.messageLabel}</Label>
            <Textarea
              id="support-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={s.messagePlaceholder}
              maxLength={4000}
              rows={6}
            />
          </div>

          <Button type="submit" disabled={submitting} className="w-full gap-2">
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
            {submitting ? s.submitting : s.submitButton}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
